import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirGtapsCommonTemplateService } from './fir-gtaps-common-template.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-gtaps-common-template',
    templateUrl: './fir-gtaps-common-template.component.html',
    styleUrls: ['./fir-gtaps-common-template.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirGtapsCommonTemplateComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  loading = false;
  showData = false;
  summary: any = {};
  funcCurrency = '';
  private isInitialized = false;

  constructor(
    private service: FirGtapsCommonTemplateService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => { this.resetAndTryInit(); })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.resetAndTryInit(); })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) { fp = extractObject(hydrated); }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell, globalParams: mergedGp, filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api)) return;
    this.isInitialized = true;
    this.refresh();
  }

  private params(): any {
    return {
      fir_ho_leid: this.filterParams?.fir_ho_leid,
      filing_key: this.filterParams?.filing_key,
      me_string: this.filterParams?.me_string,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO
    };
  }

  refresh(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    this.loading = true;
    this.showData = true;
    const params = this.params();
    this.service.getPtiSummary(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (ptiRes: any) => {
        const ptiRows = ptiRes?.jsonObject || [];
        this.parsePti(ptiRows);
        this.service.getPtepSummary(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (ptepRes: any) => {
            this.parsePtep((ptepRes?.jsonObject || [])[0] || {});
            this.loading = false;
            this.cdr.detectChanges();
          },
          error: () => { this.loading = false; this.cdr.detectChanges(); }
        });
      },
      error: () => { this.loading = false; this.cdr.detectChanges(); }
    });
  }

  private parsePti(rows: any[]): void {
    if (!rows.length) return;
    const fx = this.safeJson(rows[0]?.GET_FX_RATE_JSON)[0] || {};
    const basis = this.safeJson(rows[0]?.GET_PTI_BASIS_TOT_AMT_JSON)[0] || {};
    const pre = this.safeJson(rows[0]?.PRE_TOTALS_JSON)[0] || {};
    const ep = this.safeJson(rows[0]?.EP_SUMMARY_INFO_JSON)[0] || {};
    this.funcCurrency = rows[0]?.HO_FUNCTIONAL_CURRENCY || '';
    this.summary = {
      ...this.summary,
      total959c3EPPoolFuncCurr: ep?.POST_1986C3EP_FC || 0,
      totalNH959c3EPPoolFuncCurr: ep?.POST_HD_1986C3EP_FC || 0,
      total959c3EPPoolUSD: ep?.POST_1986C3EPUSD || 0,
      totalNH959c3EPPoolUSD: ep?.POST_HD_1986C3EPUSD || 0,
      totalTaxPool: ep?.POST_1986C3TAXES || 0,
      totalNHTaxPool: ep?.POST_HD_1986C3TAXES || 0,
      hovering959c3Ep: ep?.HD_EP_FC || 0,
      hovering959c3Taxes: ep?.HD_TAX_FC || 0,
      totalPtiUSD: ep?.PTIUSD || 0,
      totalUSDBasisInPti: rows[0]?.PTI_BASIS_TOTAL_AMT || 0,
      effectiveRateOn959c3TotalEp: ep?.ETRONPOST_1986C3EP || 0,
      effectiveRateOn959c3TotalEpNH: ep?.ETRONPOST_HD_1986C3EP || 0,
      recaptureAccounts: rows[0]?.RECAPTURE_ACCT_COUNT ?? 0,
      qualifiedDeficits: rows[0]?.QUAL_DEF_COUNT ?? 0,
      pre87EPAmt: pre?.PRE_87_EP_AMT || 0,
      pre87TaxAmt: pre?.PRE_87_TAX_AMT || 0,
      fxRate: fx?.END_RATE || 0,
      usdBasisInPTI: basis?.TOTAL || 0
    };
  }

  private parsePtep(r: any): void {
    this.summary = {
      ...this.summary,
      c1a965a_amt: r?.C1A965A_AMT || 0, c2965a_amt: r?.C2965A_AMT || 0, tot_965a_ptp_fc_amt: r?.TOT_965A_PTP_FC_AMT || 0, tot_965a_ptp_fc_usd: r?.TOT_965A_PTP_FC_USD || 0, total_965a_pti_amt: r?.TOTAL_965A_PTI_AMT || 0, ptep_ex_gain_loss_965a: r?.PTEP_EX_GAIN_LOSS_965A || 0,
      c1a965b4_amt: r?.C1A965B4_AMT || 0, c2965b4_amt: r?.C2965B4_AMT || 0, tot_965b4_ptp_fc_amt: r?.TOT_965B4_PTP_FC_AMT || 0, tot_965b4_ptp_usd: r?.TOT_965B4_PTP_USD || 0,
      c1ana_amt: r?.C1ANA_AMT || 0, total_c1ana_fc_amt: r?.TOTAL_C1ANA_FC_AMT || 0, total_c1ana_usd_amt: r?.TOTAL_C1ANA_USD_AMT || 0, total_c1ana_pti_amt: r?.TOTAL_C1ANA_PTI_AMT || 0, ptep_ex_gain_loss_c1ana: r?.PTEP_EX_GAIN_LOSS_C1ANA || 0,
      c1bna_amt: r?.C1BNA_AMT || 0, total_c1bna_fc_amt: r?.TOTAL_C1BNA_FC_AMT || 0, total_c1bna_usd_amt: r?.TOTAL_C1BNA_USD_AMT || 0, total_c1bna_pti_amt: r?.TOTAL_C1BNA_PTI_AMT || 0, ptep_ex_gain_loss_c1bna: r?.PTEP_EX_GAIN_LOSS_C1BNA || 0,
      c1a951a_amt: r?.C1A951A_AMT || 0, c2951a_amt: r?.C2951A_AMT || 0, tot_951a_fc_amt: r?.TOT_951A_FC_AMT || 0, tot_951a_usd_amt: r?.TOT_951A_USD_AMT || 0, total_951a_pti_amt: r?.TOTAL_951A_PTI_AMT || 0, ptep_ex_gain_loss_951a: r?.PTEP_EX_GAIN_LOSS_951A || 0,
      c1a951a1a_amt: r?.C1A951A1A_AMT || 0, c2951a1a_amt: r?.C2951A1A_AMT || 0, tot_951a1a_fc_amt: r?.TOT_951A1A_FC_AMT || 0, tot_951a1a_usd_amt: r?.TOT_951A1A_USD_AMT || 0, total_951a1a_pti_amt: r?.TOTAL_951A1A_PTI_AMT || 0, ptep_ex_gain_loss_951a1a: r?.PTEP_EX_GAIN_LOSS_951A1A || 0,
      c1a245ae2_amt: r?.C1A245AE2_AMT || 0, c2245ae2_amt: r?.C2245AE2_AMT || 0, tot_245ae2_fc_amt: r?.TOT_245AE2_FC_AMT || 0, tot_245ae2_usd_amt: r?.TOT_245AE2_USD_AMT || 0, total_245ae2_pti_amt: r?.TOTAL_245AE2_PTI_AMT || 0, ptep_ex_gain_loss_245ae2: r?.PTEP_EX_GAIN_LOSS_245AE2 || 0,
      c1a959e_amt: r?.C1A959E_AMT || 0, c2959e_amt: r?.C2959E_AMT || 0, tot_959e_fc_amt: r?.TOT_959E_FC_AMT || 0, tot_959e_usd_amt: r?.TOT_959E_USD_AMT || 0,
      c1a964e4_amt: r?.C1A964E4_AMT || 0, c2964e4_amt: r?.C2964E4_AMT || 0, total_964e4_fc_amt: r?.TOTAL_964E4_FC_AMT || 0, total_964e4_usd_amt: r?.TOTAL_964E4_USD_AMT || 0,
      c1_ptep_total_amt: r?.C1_PTEP_TOTAL_AMT || 0, c2_ptep_total_amt: r?.C2_PTEP_TOTAL_AMT || 0, c1_c2_ptep_total_amt: r?.C1_C2_PTEP_TOTAL_AMT || 0, c1_c2_ptep_total_amt_usd: r?.C1_C2_PTEP_TOTAL_AMT_USD || 0, us_basis_total_amt: r?.US_BASIS_TOTAL_AMT || 0, total_ptep_ex_gain_loss: r?.TOTAL_PTEP_EX_GAIN_LOSS || 0
    };
  }

  private safeJson(v: any): any[] {
    try { return Array.isArray(v) ? v : safeJsonInput<any>(v || '[]', null, 'fir-gtaps-common-template.parse1'); } catch { return []; }
  }
}
